import { useScroll } from "ahooks";
import cls from "classnames";

import { PropsWithChildren, useEffect, useRef, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Fade, IconButton, useTheme } from "@mui/material";

import usePlatform from "@/hooks/usePlatform";

import styles from "./index.module.css";

interface Props extends PropsWithChildren {
  enableScrollbar?: boolean;
}

const HorizontalScrollBar = (props: Props) => {
  const { enableScrollbar = true } = props;
  const theme = useTheme();

  const scrollRef = useRef<HTMLDivElement>(null);

  const position = useScroll(scrollRef);

  const [isOver, setIsOver] = useState(false);

  const { isPc } = usePlatform();

  const [showLeftIcon, setShowLeftIcon] = useState(false);
  const [showRightIcon, setShowRightIcon] = useState(false);

  useEffect(() => {
    if (position && scrollRef.current) {
      const scrollWidth = scrollRef.current.scrollWidth;
      const clientWidth = scrollRef.current.clientWidth;

      setShowLeftIcon(position.left > 5 ? true : false);
      setShowRightIcon(position.left < scrollWidth - clientWidth ? true : false);
    }
  }, [position]);

  const handleMove = (value: number) => {
    if (scrollRef.current) {
      const scrollWidth = scrollRef.current.scrollWidth;
      const clientWidth = scrollRef.current.clientWidth;
      const distance =
        value > 0
          ? Math.min(scrollRef.current.scrollLeft + clientWidth / 2, scrollWidth - clientWidth)
          : Math.max(scrollRef.current.scrollLeft - clientWidth / 2, 0);
      scrollRef.current?.scrollTo({ left: distance, behavior: "smooth" });
    }
  };

  return (
    <div
      className="relative"
      onMouseOver={() => setIsOver(true)}
      onMouseLeave={() => setIsOver(false)}
    >
      <div
        ref={scrollRef}
        className={cls([enableScrollbar ? "overflow-x-auto" : "", styles.noScrollbar])}
      >
        {props.children}
      </div>

      <Fade in={enableScrollbar && isPc && isOver && showLeftIcon}>
        <div className="absolute left-[10px] top-[50%] translate-y-[-50%]">
          <IconButton
            aria-label="left"
            size="small"
            sx={{
              background: theme.palette.primary.main,
              boxShadow: 2,
              "&:hover": { background: theme.palette.primary.main },
            }}
            onClick={() => handleMove(-1)}
          >
            <ChevronLeftIcon fontSize="inherit" sx={{ color: "white" }} />
          </IconButton>
        </div>
      </Fade>

      <Fade in={enableScrollbar && isPc && isOver && showRightIcon}>
        <div className="absolute right-[10px] top-[50%] translate-y-[-50%]">
          <IconButton
            aria-label="right"
            size="small"
            sx={{
              background: theme.palette.primary.main,
              boxShadow: 2,
              "&:hover": { background: theme.palette.primary.main },
            }}
            onClick={() => handleMove(1)}
          >
            <ChevronRightIcon fontSize="inherit" sx={{ color: "white" }} />
          </IconButton>
        </div>
      </Fade>
    </div>
  );
};

export default HorizontalScrollBar;
